import React, { useEffect, useState, useRef } from 'react';
import { PlusIcon, TrashIcon, GitBranchIcon, PlusCircleIcon, BookOpenIcon } from 'lucide-react';
export function LogicGroupBlock({
  condition,
  level = 0,
  children,
  onAddCondition,
  onAddNestedGroup,
  onAddTemplate,
  onToggleOperator,
  onRemove,
  reusableConditions
}) {
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  // Create refs for the menu buttons and dropdown contents
  const addMenuButtonRef = useRef(null);
  const addMenuContentRef = useRef(null);
  const templateMenuButtonRef = useRef(null);
  const templateMenuContentRef = useRef(null);
  // Effect for handling clicks outside the menus
  useEffect(() => {
    const handleClickOutside = event => {
      // Handle Add Menu
      if (showAddMenu && !addMenuButtonRef.current?.contains(event.target) && !addMenuContentRef.current?.contains(event.target)) {
        setShowAddMenu(false);
      }
      // Handle Template Menu
      if (showTemplateMenu && !templateMenuButtonRef.current?.contains(event.target) && !templateMenuContentRef.current?.contains(event.target)) {
        setShowTemplateMenu(false);
      }
    };
    // Add event listener when menus are open
    if (showAddMenu || showTemplateMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    // Clean up event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAddMenu, showTemplateMenu]);
  // Determine background color based on operator and level
  const getBgColor = () => {
    if (condition.operator === 'AND') {
      return level % 2 === 0 ? 'bg-green-50' : 'bg-green-100';
    } else {
      return level % 2 === 0 ? 'bg-blue-50' : 'bg-blue-100';
    }
  };
  // Determine border color based on operator
  const getBorderColor = () => {
    return condition.operator === 'AND' ? 'border-green-300' : 'border-blue-300';
  };
  // Determine header background color based on operator
  const getHeaderBgColor = () => {
    return condition.operator === 'AND' ? 'bg-green-100' : 'bg-blue-100';
  };
  return <div className={`border ${getBorderColor()} rounded-md overflow-hidden mb-3`} data-id="element-11650">
      <div className={`px-4 py-2 ${getHeaderBgColor()} flex justify-between items-center`} data-id="element-11651">
        <div className="flex items-center" data-id="element-11652">
          <GitBranchIcon className="h-4 w-4 mr-2 text-gray-600" data-id="element-11653" />
          <button type="button" onClick={onToggleOperator} className={`px-3 py-1 rounded-md text-sm font-medium ${condition.operator === 'AND' ? 'bg-green-200 text-green-800 hover:bg-green-300' : 'bg-blue-200 text-blue-800 hover:bg-blue-300'}`} data-id="element-11654">
            {condition.operator}
          </button>
        </div>
        <div className="flex items-center space-x-2" data-id="element-11655">
          <div className="relative" data-id="element-11656">
            <button ref={addMenuButtonRef} type="button" onClick={() => {
            setShowAddMenu(!showAddMenu);
            setShowTemplateMenu(false);
          }} className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11657">
              <PlusIcon className="h-3 w-3 mr-1" data-id="element-11658" />
              Add
            </button>
            {showAddMenu && <div ref={addMenuContentRef} className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10" data-id="element-11659">
                <div className="py-1" role="menu" aria-orientation="vertical" data-id="element-11660">
                  <button type="button" className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => {
                onAddCondition();
                setShowAddMenu(false);
              }} data-id="element-11661">
                    Add Condition
                  </button>
                  <button type="button" className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => {
                onAddNestedGroup('AND');
                setShowAddMenu(false);
              }} data-id="element-11662">
                    Add AND Group
                  </button>
                  <button type="button" className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => {
                onAddNestedGroup('OR');
                setShowAddMenu(false);
              }} data-id="element-11663">
                    Add OR Group
                  </button>
                </div>
              </div>}
          </div>
          <div className="relative" data-id="element-11664">
            <button ref={templateMenuButtonRef} type="button" onClick={() => {
            setShowTemplateMenu(!showTemplateMenu);
            setShowAddMenu(false);
          }} className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11665">
              <BookOpenIcon className="h-3 w-3 mr-1" data-id="element-11666" />
              Templates
            </button>
            {showTemplateMenu && reusableConditions.length > 0 && <div ref={templateMenuContentRef} className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10" data-id="element-11667">
                <div className="py-1" role="menu" aria-orientation="vertical" data-id="element-11668">
                  {reusableConditions.map(template => <button key={template.id} type="button" className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => {
                onAddTemplate(template);
                setShowTemplateMenu(false);
              }} data-id="element-11669">
                      <div className="font-medium" data-id="element-11670">{template.name}</div>
                      <div className="text-xs text-gray-500" data-id="element-11671">
                        {template.description}
                      </div>
                    </button>)}
                </div>
              </div>}
          </div>
          {level > 0 && <button type="button" onClick={onRemove} className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-11672">
              <TrashIcon className="h-3 w-3" data-id="element-11673" />
            </button>}
        </div>
      </div>
      <div className={`${getBgColor()} p-2`} data-id="element-11674">
        <div className="space-y-2" data-id="element-11675">{children}</div>
        <div className="mt-3 flex justify-center" data-id="element-11676">
          <button type="button" onClick={onAddCondition} className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11677">
            <PlusCircleIcon className="h-3 w-3 mr-1" data-id="element-11678" />
            Add Condition
          </button>
        </div>
      </div>
    </div>;
}